import { useEffect, useMemo, useState } from 'react';
import { useParams } from 'react-router-dom';
import clsx from 'clsx';
import activityStyles from '../_shared/ActivityCard.module.scss';
import { fetchCompaniesNetworkData, fetchLenderConnectionsData } from '../../../../store/dashboard/overview/overview_apiHelper';
import { autoTransmission } from '../../../../assets/hostedassets';
import NetworkIcon from '../../../../assets/Svg/NetworkIcon';
import ActivityCountPill from '../_shared/ActivityCountPill';
import { getDealFormConfig } from '../../../../store/investor/apiHelpers';
import { useAppSelector } from '../../../../app/hooks';
import { VIEW_CONFIGS } from '../../../../utils/constants/commonConstants';
import { CompaniesNetworkData, StatusOption } from '../../DashboardModules/Overview/components/RelatedCompaniesNetwork/types';
import RelatedCompaniesNetworkDrawer from '../../DashboardModules/Overview/components/RelatedCompaniesNetwork/RelatedCompaniesNetworkDrawer';
import { Mixpanel } from '../../../../utils/mixpanel';
import { AICA_NETWORK_DRAWER_OPENED } from '../../../../utils/constants/mixpanelEvents/investorEvents';
import { useIsMobile } from '../../../../common/_custom/Hooks/useIsMobile';
import Button from '../../../../common/_custom/Button/Button';
import { useRelatedPartiesLock } from './useRelatedPartiesLock';

interface CompaniesNetworkButtonProps {
    companyName?: string;
    isRelatedPartiesLocked?: boolean;
    variant?: 'sidebar' | 'card' | 'header';
}

const CompaniesNetworkButton = ({ companyName, isRelatedPartiesLocked = false, variant = 'sidebar' }: CompaniesNetworkButtonProps) => {
    const params = useParams();
    const assesseeOrgId = params?.id;

    const userData = useAppSelector((state) => state.user.userData);
    const investorConfig = useAppSelector((state) => state.investor.config);
    // Desktop keeps the live card design; mobile renders the redesigned card.
    const isMobile = useIsMobile();

    const [data, setData] = useState<CompaniesNetworkData | null>(null);
    const [lenderConnections, setLenderConnections] = useState<any[]>([]);
    const [isLoading, setIsLoading] = useState(false);
    const [isDrawerOpen, setIsDrawerOpen] = useState(false);
    const [localStatusOptions, setLocalStatusOptions] = useState<Record<string, StatusOption>>({});
    // Bumped on every open so the header variant re-reads the related-parties lock each time.
    // The lock is only read once this company's drawer has been opened (the parent remounts per company).
    const [drawerOpenCount, setDrawerOpenCount] = useState(0);
    const lockOrgId = variant === 'header' && drawerOpenCount > 0 ? assesseeOrgId : undefined;
    const headerRelatedPartiesLocked = useRelatedPartiesLock(lockOrgId, drawerOpenCount);
    const [localRelationshipStatusOptions, setLocalRelationshipStatusOptions] = useState<Record<string, StatusOption>>({});

    useEffect(() => {
        if (!assesseeOrgId) return;
        let cancelled = false;
        setIsLoading(true);
        Promise.all([fetchCompaniesNetworkData({ orgId: assesseeOrgId }), fetchLenderConnectionsData({ orgId: assesseeOrgId })])
            .then(([networkResult, lenders]) => {
                if (!cancelled) {
                    setData(networkResult ?? null);
                    setLenderConnections(lenders || []);
                }
            })
            .finally(() => {
                if (!cancelled) setIsLoading(false);
            });
        return () => {
            cancelled = true;
        };
    }, [assesseeOrgId, fetchCompaniesNetworkData, setData, setIsLoading]);

    const extractStatusOptions = (config: any, field: string): Record<string, StatusOption> => {
        const columns = config?.leadsColumns?.columns || [];
        const col = columns.find((c: any) => c?.field === field);
        return col?.dropdownOptions || {};
    };

    const configStatusOptions = extractStatusOptions(investorConfig?.additionalDetailsConfig, 'companyStatus');
    const configRelationshipStatusOptions = extractStatusOptions(
        investorConfig?.additionalDetailsConfig,
        'customFields.relationshipStatus',
    );

    const statusOptionsSource = Object.keys(localStatusOptions).length > 0 ? localStatusOptions : configStatusOptions;
    const relationshipStatusOptionsSource =
        Object.keys(localRelationshipStatusOptions).length > 0 ? localRelationshipStatusOptions : configRelationshipStatusOptions;

    const statusOptions = useMemo<StatusOption[]>(() => Object.values(statusOptionsSource), [statusOptionsSource]);
    const relationshipStatusOptions = useMemo<StatusOption[]>(
        () => Object.values(relationshipStatusOptionsSource),
        [relationshipStatusOptionsSource],
    );

    useEffect(() => {
        const hasOptions = statusOptions.length > 0 || relationshipStatusOptions.length > 0;
        if (isLoading || hasOptions) return;
        if (!userData?.accountType || !userData?.investorOrgId || !userData?.primaryEmail) return;

        getDealFormConfig(
            {
                accountType: userData.accountType,
                investorOrgId: userData.investorOrgId,
                primaryEmail: userData.primaryEmail,
                configName: VIEW_CONFIGS.AICA_DEAL_NAVBAR_VIEW,
            },
            {
                onSuccess: (additionalDetailsConfig) => {
                    setLocalStatusOptions(extractStatusOptions(additionalDetailsConfig, 'companyStatus'));
                    setLocalRelationshipStatusOptions(extractStatusOptions(additionalDetailsConfig, 'customFields.relationshipStatus'));
                },
            },
        );
    }, [
        isLoading,
        statusOptions.length,
        relationshipStatusOptions.length,
        userData?.accountType,
        userData?.investorOrgId,
        userData?.primaryEmail,
    ]);

    const relatedPartiesCount = Object.keys(data?.networkRelatedPartyEntities ?? {}).length;
    const totalCount = (data?.totalCompaniesInNetwork ?? 0) + (lenderConnections?.length ?? 0) + relatedPartiesCount;
    const canOpen = !isLoading && data !== null;

    const handleOpenDrawer = () => {
        if (!canOpen) return;
        setDrawerOpenCount((count) => count + 1);
        setIsDrawerOpen(true);
        Mixpanel.track(AICA_NETWORK_DRAWER_OPENED, {
            userEmail: userData?.primaryEmail,
            companyName,
            pnoId: userData?.investorOrgId,
            totalConnectionsCount: totalCount,
            source: variant,
        });
    };

    const isZeroConnections = !isLoading && totalCount === 0;
    // Nothing worth opening: still loading, or loaded with no connections. Drives both
    // the sidebar row's disabled state and the muted pill tone on every desktop
    // surface, so the pill never flashes blue on "..." before the counts resolve.
    const hasNothingToShow = !canOpen || isZeroConnections;
    // While the counts are still loading the pill shows "..." and is greyed, so the
    // card's hover text must not offer an action it cannot perform yet. Undefined
    // leaves the placeholder in place instead of swapping in "View connection".
    let cardHoverText: string | undefined;
    if (!isLoading) cardHoverText = isZeroConnections ? 'No connections' : 'View connection';

    // Sidebar row: label + the shared count pill. Same button and the same zero-state
    // as the Overview card — grey pill and no click, so it cannot open a drawer with
    // nothing in it — but static, with no hover swap. (The mobile card deliberately
    // still opens at zero, on the grounds that lookalikes may exist; that pre-dates
    // this row and is untouched.)
    const renderSidebarVariant = () => (
        <button type="button" className={activityStyles.SidebarChip} onClick={handleOpenDrawer} disabled={hasNothingToShow}>
            <span className={activityStyles.SidebarLabel} title="Connections">
                Connections
            </span>
            <ActivityCountPill tone="connections" count={isLoading ? '...' : totalCount} muted={hasNothingToShow} compactArrow />
        </button>
    );

    // Company top bar: "Network (N)", disabled and muted when there is nothing to open.
    const renderHeaderVariant = () => (
        <button type="button" className={activityStyles.HeaderChip} onClick={handleOpenDrawer} disabled={hasNothingToShow}>
            <NetworkIcon width="20" height="20" colour="currentColor" aria-hidden />
            <span>{`Network (${isLoading ? '...' : totalCount})`}</span>
        </button>
    );

    // Mobile: redesigned card (title row on top + full-width colored count row).
    const renderCardMobile = () => (
        <Button
            type="button"
            className={clsx(activityStyles.ConnectionsSummaryCard, activityStyles.MobileCard, {
                [activityStyles.Disabled]: !canOpen,
            })}
            onClick={handleOpenDrawer}
            disabled={!canOpen}
            text={
                <>
                    <div className={activityStyles.CardHeader}>
                        <img className={activityStyles.Icon} src={autoTransmission} alt="" />
                        <span className={activityStyles.Title}>Connections</span>
                    </div>
                    <ActivityCountPill tone="connections" count={isLoading ? '...' : totalCount} muted={!canOpen} fullWidth />
                </>
            }
        />
    );

    // Desktop: the live card design (icon + title + description, right-side count/hover-view).
    const renderCardDesktop = () => (
        <div
            className={clsx(activityStyles.ConnectionsSummaryCard, { [activityStyles.Disabled]: isZeroConnections })}
            onClick={isZeroConnections ? undefined : handleOpenDrawer}
        >
            <div className={activityStyles.Left}>
                <div className={activityStyles.LeftTop}>
                    <img className={activityStyles.Icon} src={autoTransmission} alt="" />
                    <span className={activityStyles.Title}>Connections</span>
                </div>
                <span className={activityStyles.Description}>Lookalikes, Vendors, Lender, Customer &amp; Related Parties on AICA</span>
            </div>
            <ActivityCountPill
                tone="connections"
                count={isLoading ? '...' : totalCount}
                muted={hasNothingToShow}
                hoverText={cardHoverText}
            />
        </div>
    );

    const renderCardVariant = () => (isMobile ? renderCardMobile() : renderCardDesktop());

    return (
        <>
            {variant === 'card' && renderCardVariant()}
            {variant === 'sidebar' && renderSidebarVariant()}
            {variant === 'header' && renderHeaderVariant()}

            {isDrawerOpen && data && (
                <RelatedCompaniesNetworkDrawer
                    data={data}
                    lenderConnections={lenderConnections}
                    onClose={() => setIsDrawerOpen(false)}
                    statusOptions={statusOptions}
                    relationshipStatusOptions={relationshipStatusOptions}
                    companyName={companyName}
                    isRelatedPartiesLocked={variant === 'header' ? headerRelatedPartiesLocked : isRelatedPartiesLocked}
                    assesseeOrgId={assesseeOrgId}
                />
            )}
        </>
    );
};

export default CompaniesNetworkButton;
